import { addMinutes, format, formatDistanceToNow, isPast } from "date-fns";
import React from "react";
import { t } from "ttag";

import { Context, type templates } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";

import { formatEventType, getTZShortname } from "../../utils/events";
import { ButtonPrimary } from "../Clickables";
import { H } from "../Headings";
import { RichText } from "../RichText";
import { Separator } from "../Separator";
import { Share } from "../Share";

import styles from "./event.module.scss";

type TemplateProps = Pick<
    templates.EventPageTemplate["page"],
    | "event_type"
    | "title"
    | "short_description"
    | "attendance_details"
    | "live_datetime"
    | "duration"
    | "register_url"
    | "seo_image"
>;

export const EventHero = (props: TemplateProps) => {
    const eventStart = props.live_datetime
        ? new Date(props.live_datetime)
        : null;
    const eventEnd =
        eventStart && props.duration
            ? addMinutes(eventStart, props.duration)
            : null;
    const { request } = React.useContext(Context);
    return (
        <div className={styles.root}>
            {props.seo_image?.block && (
                <div className={styles.heroImage}>
                    <ImageChooserBlock value={props.seo_image.block} />
                </div>
            )}
            <Separator orientation="vertical" />
            <div className={styles.heroText}>
                <div>
                    <span className={styles.pill}>
                        {formatEventType(props.event_type)}
                    </span>
                    {eventStart && (
                        <span className={styles.distance}>
                            {isPast(eventStart)
                                ? t`Started ${formatDistanceToNow(eventStart)} ago`
                                : t`Starts in ${formatDistanceToNow(eventStart)}`}
                        </span>
                    )}
                </div>
                <H className={styles.title}>{props.title}</H>
                <RichText
                    value={props.short_description.value}
                    className={styles.subtitle}
                    blockOverrides={{
                        unstyled: {
                            element: "p",
                            props: { className: styles.subtitleText },
                        },
                    }}
                />
                {eventStart && (
                    <p className={styles.attendance}>
                        <span>{format(eventStart, "eee, MMM d, yyyy")}</span>
                        <span>
                            {format(eventStart, " h:mm a")}
                            {eventEnd &&
                                ` - ${format(eventEnd, "h:mm a")}`}{" "}
                            {getTZShortname(eventStart)}
                        </span>
                    </p>
                )}
                {props.attendance_details.value.blocks.length > 0 && (
                    <RichText
                        value={props.attendance_details.value}
                        blockOverrides={{
                            unstyled: {
                                element: "p",
                                props: { className: styles.attendance },
                            },
                        }}
                    />
                )}
                <div className={styles.buttons}>
                    {props.register_url && (
                        <ButtonPrimary href={props.register_url}>
                            {t`Register`}
                        </ButtonPrimary>
                    )}
                    <Share
                        url={
                            props.register_url
                                ? props.register_url
                                : request.url
                        }
                    />
                </div>
            </div>
        </div>
    );
};
